import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { ArrowLeft, ArrowRight, RotateCcw, ShieldCheck, Plus, Trash2, CheckCircle2, CalendarDays, Upload, IdCard, Lock } from "lucide-react";
import { BRAND } from "../lib/brand";
import { WAIVER_SECTIONS } from "../lib/waiverText";
import { useT, LanguageToggle } from "../lib/i18n";

const emptyMinor = () => ({ name: "", birthdate: "", relation: "" });
const DRAFT_KEY = "rar_waiver_draft_v1";
const API = process.env.REACT_APP_BACKEND_URL;

export default function Waiver() {
  const { t } = useT();
  const sigRef = useRef(null);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // --- Access gate (server-verified shared code; protects ID/signature page) ---
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem("rar_waiver_unlocked") === "1"
  );
  const [gateCode, setGateCode] = useState("");
  const [gateErr, setGateErr] = useState("");
  const [gateLoading, setGateLoading] = useState(false);

  const verifyAccess = async (e) => {
    e.preventDefault();
    setGateErr("");
    if (!gateCode.trim()) {
      setGateErr("Please enter the access code.");
      return;
    }
    setGateLoading(true);
    try {
      const res = await fetch(`${API}/api/waiver/verify-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: gateCode.trim() }),
      });
      if (res.ok) {
        sessionStorage.setItem("rar_waiver_unlocked", "1");
        setUnlocked(true);
      } else {
        setGateErr("Incorrect access code. Please check with Rockstar ATV Rentals.");
      }
    } catch (err) {
      setGateErr("Couldn't verify the code. Check your connection and try again.");
    } finally {
      setGateLoading(false);
    }
  };

  const [form, setForm] = useState(() => {
    const defaults = {
      fullName: "",
      dob: "",
      phone: "",
      email: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      helmet: "", // 'agree' | 'refuse'
      agreeRelease: false,
      guardianConsent: false,
    };
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      return saved?.form ? { ...defaults, ...saved.form } : defaults;
    } catch (_) {
      return defaults;
    }
  });
  const [minors, setMinors] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      return Array.isArray(saved?.minors) ? saved.minors : [];
    } catch (_) {
      return [];
    }
  });

  // --- Autosave draft (text fields only; ID/signature never cached for privacy) ---
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, minors }));
    } catch (_) {
      /* storage unavailable */
    }
  }, [form, minors]);
  const [idImage, setIdImage] = useState(""); // base64 for payload
  const [idPreview, setIdPreview] = useState(""); // displayable preview (may be empty for HEIC)
  const [idName, setIdName] = useState("");
  const [idLoading, setIdLoading] = useState(false);

  const handleIdUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const looksImage =
      (file.type && file.type.startsWith("image/")) ||
      /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name || "");
    if (!looksImage) {
      setError("Please upload an image file (JPG, PNG, HEIC).");
      return;
    }
    setError("");
    setIdLoading(true);
    setIdName(file.name || "photo");
    setIdImage("");
    setIdPreview("");

    const reader = new FileReader();
    reader.onload = (ev) => {
      const raw = ev.target.result; // original base64 data URL
      const img = new Image();
      img.onload = () => {
        try {
          const maxDim = 1400;
          let { width, height } = img;
          if (!width || !height) throw new Error("no-dimensions");
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          const jpeg = canvas.toDataURL("image/jpeg", 0.8);
          setIdImage(jpeg);
          setIdPreview(jpeg);
        } catch (err) {
          // decoding worked but canvas failed — keep original for the record
          setIdImage(raw);
          setIdPreview(raw);
        }
        setIdLoading(false);
      };
      img.onerror = () => {
        // Cannot decode in this browser (common with iPhone HEIC on non-Safari).
        // Keep the original file so it still reaches the sheet; skip visual preview.
        setIdImage(raw);
        setIdPreview("");
        setIdLoading(false);
      };
      img.src = raw;
    };
    reader.onerror = () => {
      setIdLoading(false);
      setError("Couldn't read that photo. Please try a different image.");
    };
    reader.readAsDataURL(file);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMinor = (i, k, v) =>
    setMinors((arr) => arr.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const addMinor = () => setMinors((arr) => (arr.length < 4 ? [...arr, emptyMinor()] : arr));
  const removeMinor = (i) => setMinors((arr) => arr.filter((_, idx) => idx !== i));

  const clearSig = () => {
    sigRef.current?.clear();
    setSigned(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const required = ["fullName", "dob", "phone", "email", "address", "city", "state", "zip"];
    if (required.some((k) => !form[k].trim())) {
      setError("Please complete all required participant fields.");
      return;
    }
    if (!form.helmet) {
      setError("Please select a helmet option.");
      return;
    }
    if (!idImage) {
      setError("Please upload a photo of your ID, License, or Passport.");
      return;
    }
    if (!form.agreeRelease) {
      setError("You must agree to the Participant Agreement, Release and Assumption of Risk.");
      return;
    }
    if (minors.length > 0 && !form.guardianConsent) {
      setError("As the guardian, you must agree to the Perpetual Waiver for the minors listed.");
      return;
    }
    if (sigRef.current?.isEmpty()) {
      setError("Please sign with your finger or mouse to continue.");
      return;
    }

    const signature = sigRef.current.getCanvas().toDataURL("image/png");
    const payload = {
      token: BRAND.waiverToken,
      submittedAt: new Date().toISOString(),
      fullName: form.fullName,
      dob: form.dob,
      phone: form.phone,
      email: form.email,
      address: form.address,
      city: form.city,
      state: form.state,
      zip: form.zip,
      helmet: form.helmet === "refuse" ? "REFUSED headgear (adult)" : "Will wear DOT/SNELL helmet",
      agreedParticipantRelease: form.agreeRelease ? "YES" : "NO",
      guardianConsentForMinors: minors.length ? (form.guardianConsent ? "YES" : "NO") : "N/A",
      minors: minors
        .map((m, i) => `#${i + 1} ${m.name} | DOB ${m.birthdate} | ${m.relation}`)
        .join("  ||  "),
      signature,
      idImage,
      idFileName: idName,
      source: "rockstaratvrentals.com/waiver",
    };

    setSubmitting(true);
    try {
      // 1) Save to our own backend (feeds the admin dashboard + push notifications)
      const backendPayload = {
        fullName: form.fullName,
        dob: form.dob,
        phone: form.phone,
        email: form.email,
        address: form.address,
        city: form.city,
        state: form.state,
        zip: form.zip,
        helmet:
          form.helmet === "refuse"
            ? "REFUSED headgear (adult)"
            : "Will wear DOT/SNELL helmet",
        agreeRelease: !!form.agreeRelease,
        guardianConsent: !!form.guardianConsent,
        minors: minors.map((m) => ({
          name: m.name || "",
          birthdate: m.birthdate || "",
          relation: m.relation || "",
        })),
        signature,
        idImage,
        idFileName: idName,
      };
      // Fire both in parallel; don't block waiver on Sheet failure
      const backendReq = fetch(`${API}/api/waivers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(backendPayload),
      });
      const sheetReq = BRAND.waiverSheetWebhook
        ? fetch(BRAND.waiverSheetWebhook, {
            method: "POST",
            mode: "no-cors",
            headers: { "Content-Type": "text/plain;charset=utf-8" },
            body: JSON.stringify(payload),
          })
        : Promise.resolve();
      await Promise.allSettled([backendReq, sheetReq]);
      setSubmitted(true);
      try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* noop */ }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Something went wrong submitting your waiver. Please try again or text us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="waiver-gate">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="flex flex-col items-center text-center">
            <div className="bg-black rounded-full p-1 ring-1 ring-white/15 mb-6">
              <img src={BRAND.logo} alt="Rockstar ATVs" className="h-14 w-14 rounded-full" />
            </div>
            <span className="inline-flex items-center gap-2 border border-[color:var(--primary)]/50 text-[color:var(--primary)] px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-mono mb-5">
              <Lock size={12} /> {t("waiver.lock.title")}
            </span>
            <h1 className="font-display uppercase text-4xl md:text-5xl leading-[0.95]">
              {t("waiver.lock.title")}
            </h1>
            <p className="mt-4 text-sm text-white/60 leading-relaxed">
              {t("waiver.lock.subtitle")}{" "}
              <a href={`sms:${BRAND.phoneRaw}`} className="text-[color:var(--primary)] link-under">{BRAND.phone}</a>.
            </p>
          </div>

          <form onSubmit={verifyAccess} className="mt-8 space-y-3" data-testid="waiver-gate-form">
            <input
              type="password"
              value={gateCode}
              onChange={(e) => setGateCode(e.target.value)}
              placeholder={t("waiver.lock.placeholder")}
              autoFocus
              className="w-full bg-[color:var(--bg-elev)] border border-white/15 focus:border-[color:var(--primary)] text-white text-center tracking-[0.2em] px-4 py-3.5 outline-none transition-colors"
              data-testid="waiver-gate-input"
            />
            {gateErr && (
              <p className="text-[color:var(--primary)] text-sm font-semibold text-center" data-testid="waiver-gate-error">
                {gateErr}
              </p>
            )}
            <button
              type="submit"
              disabled={gateLoading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] disabled:opacity-60 text-white font-bold uppercase tracking-[0.16em] text-[11px] px-6 py-3.5 transition-colors"
              data-testid="waiver-gate-submit"
            >
              {gateLoading ? "…" : t("waiver.lock.submit")} <ArrowRight size={13} />
            </button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-3">
            <Link to="/" className="text-[10px] uppercase tracking-[0.22em] font-mono text-white/50 hover:text-white transition-colors inline-flex items-center gap-2">
              <ArrowLeft size={12} /> Back
            </Link>
            <LanguageToggle />
          </div>
        </motion.div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="waiver-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-lg text-center"
        >
          <CheckCircle2 size={54} className="text-[color:var(--primary)] mx-auto" strokeWidth={1.4} />
          <h1 className="mt-6 font-display uppercase text-4xl md:text-5xl leading-[0.95]">
            {t("waiver.success.title")}
          </h1>
          <p className="mt-4 text-white/70">
            {t("waiver.success.body").replace("{name}", form.fullName.split(" ")[0] || "rider")}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={BRAND.bookingUrl}
              className="inline-flex items-center justify-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-[0.16em] text-[11px] px-5 py-3.5 transition-colors"
              data-testid="waiver-success-book"
            >
              <CalendarDays size={13} /> {t("waiver.success.bookBtn")}
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:bg-white hover:text-black text-white font-bold uppercase tracking-[0.16em] text-[11px] px-5 py-3.5 transition-colors"
            >
              {t("waiver.success.homeBtn")}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="waiver-page">
      <header className="border-b border-white/10" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="waiver-home-link">
            <div className="bg-black rounded-full p-0.5 ring-1 ring-white/15">
              <img src={BRAND.logo} alt="Rockstar ATVs" className="h-8 w-8 rounded-full" />
            </div>
            <div>
              <div className="font-display text-white text-sm leading-none">ROCKSTAR</div>
              <div className="text-[9px] tracking-[0.28em] text-[color:var(--primary)] font-mono">
                ATV RENTALS · FL
              </div>
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-white/80 hover:text-[color:var(--primary)] transition-colors"
          >
            <ArrowLeft size={12} /> {t("waiver.success.homeBtn")}
          </Link>
          <div className="ml-3 hidden sm:block">
            <LanguageToggle />
          </div>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="overline mb-3">{t("waiver.overline")}</p>
          <h1 className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
            {t("waiver.title")}
          </h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base text-white/60 leading-relaxed">
            {t("waiver.subtitle")}
          </p>
        </motion.div>

        {/* Legal text */}
        <div
          className="mt-8 border border-white/10 bg-[color:var(--bg-elev)] p-5 md:p-7 max-h-[420px] overflow-y-auto space-y-6"
          data-lenis-prevent
          data-testid="waiver-legal-text"
        >
          {WAIVER_SECTIONS.map((s, i) => (
            <section key={i}>
              <h2 className="font-display uppercase text-[color:var(--primary)] text-lg md:text-xl leading-tight">
                {s.heading}
              </h2>
              {s.subheading && (
                <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/50 mt-1">
                  {s.subheading}
                </p>
              )}
              <div className="mt-3 space-y-3">
                {s.paras.map((p, k) => (
                  <p key={k} className="text-xs md:text-[13px] text-white/70 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-9" data-testid="waiver-form">
          {/* Participant */}
          <section>
            <p className="overline mb-3">{t("waiver.section.rider")}</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Input label={t("waiver.fields.fullName")} testid="waiver-name">
                <input type="text" value={form.fullName} onChange={(e) => set("fullName", e.target.value)} className={inputCls} data-testid="waiver-input-name" />
              </Input>
              <Input label={t("waiver.fields.dob")} testid="waiver-dob">
                <input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className={inputCls} data-testid="waiver-input-dob" />
              </Input>
              <Input label={t("waiver.fields.phone")} testid="waiver-phone">
                <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="waiver-input-phone" />
              </Input>
              <Input label={t("waiver.fields.email")} testid="waiver-email">
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="waiver-input-email" />
              </Input>
              <Input label={t("waiver.fields.address")} full testid="waiver-address">
                <input type="text" value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="waiver-input-address" />
              </Input>
              <Input label={t("waiver.fields.city")} testid="waiver-city">
                <input type="text" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="waiver-input-city" />
              </Input>
              <div className="grid grid-cols-2 gap-4">
                <Input label={t("waiver.fields.state")} testid="waiver-state">
                  <input type="text" value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls} data-testid="waiver-input-state" />
                </Input>
                <Input label={t("waiver.fields.zip")} testid="waiver-zip">
                  <input type="text" value={form.zip} onChange={(e) => set("zip", e.target.value)} className={inputCls} data-testid="waiver-input-zip" />
                </Input>
              </div>
            </div>
          </section>

          {/* Photo ID */}
          <section>
            <p className="overline mb-3">{t("waiver.section.id")}</p>
            <p className="text-xs text-white/50 mb-3">
              Upload a clear photo of your driver&rsquo;s license, state ID, or passport. Required to ride.
            </p>
            <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
              <label
                className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/25 hover:border-[color:var(--primary)] bg-[color:var(--bg-elev)] cursor-pointer py-8 px-4 transition-colors text-center"
                data-testid="waiver-id-dropzone"
              >
                <Upload size={22} className="text-[color:var(--primary)]" />
                <span className="text-xs uppercase tracking-widest font-mono text-white/80">
                  {idLoading ? "Processing…" : idImage ? "Replace Photo" : "Tap to Upload / Take Photo"}
                </span>
                <span className="text-[10px] text-white/40">JPG · PNG · HEIC · Max ~10MB</span>
                <input
                  type="file"
                  accept="image/*,.heic,.heif"
                  onChange={handleIdUpload}
                  className="hidden"
                  data-testid="waiver-id-input"
                />
              </label>
              <div className="w-full md:w-56 aspect-[16/10] border border-white/15 bg-black flex items-center justify-center overflow-hidden">
                {idPreview ? (
                  <img
                    src={idPreview}
                    alt="ID preview"
                    className="w-full h-full object-contain"
                    data-testid="waiver-id-preview"
                    onError={() => setIdPreview("")}
                  />
                ) : idImage ? (
                  <div className="flex flex-col items-center gap-2 text-[color:var(--primary)] px-3 text-center" data-testid="waiver-id-attached">
                    <CheckCircle2 size={26} />
                    <span className="text-[10px] uppercase tracking-widest font-mono">ID Attached</span>
                    <span className="text-[9px] text-white/50 break-all line-clamp-2">{idName}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-white/30">
                    <IdCard size={26} />
                    <span className="text-[10px] uppercase tracking-widest font-mono">ID Preview</span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Helmet */}
          <section>
            <p className="overline mb-3">{t("waiver.section.helmet")}</p>
            <div className="grid md:grid-cols-2 gap-3">
              <Radio
                name="helmet"
                checked={form.helmet === "agree"}
                onChange={() => set("helmet", "agree")}
                label={t("waiver.helmet.agree")}
                testid="waiver-helmet-agree"
              />
              <Radio
                name="helmet"
                checked={form.helmet === "refuse"}
                onChange={() => set("helmet", "refuse")}
                label={t("waiver.helmet.refuse")}
                testid="waiver-helmet-refuse"
              />
            </div>
          </section>

          {/* Minors */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <p className="overline">{t("waiver.section.minors")}</p>
              {minors.length < 4 && (
                <button type="button" onClick={addMinor} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono font-semibold text-[color:var(--primary)] hover:text-white transition-colors" data-testid="waiver-add-minor">
                  <Plus size={13} /> {t("waiver.minors.add")}
                </button>
              )}
            </div>
            <div className="space-y-3">
              {minors.map((m, i) => (
                <div key={i} className="grid md:grid-cols-[1fr_auto] gap-3 items-end border border-white/10 p-3" data-testid={`waiver-minor-${i}`}>
                  <div className="grid md:grid-cols-3 gap-3">
                    <Input label={`${t("waiver.fields.minorName")} #${i + 1}`} testid={`waiver-minor-name-${i}`}>
                      <input type="text" value={m.name} onChange={(e) => setMinor(i, "name", e.target.value)} className={inputCls} data-testid={`waiver-minor-name-input-${i}`} />
                    </Input>
                    <Input label={t("waiver.fields.minorDob")} testid={`waiver-minor-dob-${i}`}>
                      <input type="date" value={m.birthdate} onChange={(e) => setMinor(i, "birthdate", e.target.value)} className={inputCls} data-testid={`waiver-minor-dob-input-${i}`} />
                    </Input>
                    <Input label={t("waiver.fields.minorRelation")} testid={`waiver-minor-rel-${i}`}>
                      <input type="text" value={m.relation} onChange={(e) => setMinor(i, "relation", e.target.value)} className={inputCls} data-testid={`waiver-minor-rel-input-${i}`} />
                    </Input>
                  </div>
                  <button type="button" onClick={() => removeMinor(i)} className="text-white/50 hover:text-[color:var(--primary)] transition-colors pb-2" aria-label={t("waiver.minors.remove")} data-testid={`waiver-remove-minor-${i}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Agreements */}
          <section className="space-y-2.5">
            <Checkbox
              testid="waiver-agree"
              checked={form.agreeRelease}
              onChange={(v) => set("agreeRelease", v)}
              label={t("waiver.release.agree")}
            />
            {minors.length > 0 && (
              <Checkbox
                testid="waiver-guardian"
                checked={form.guardianConsent}
                onChange={(v) => set("guardianConsent", v)}
                label={t("waiver.minors.consent")}
              />
            )}
          </section>

          {/* Signature */}
          <section>
            <div className="flex items-end justify-between mb-2">
              <div>
                <p className="overline">{t("waiver.section.signature")}</p>
                <p className="text-xs text-white/60 mt-1">{t("waiver.signature.prompt")}</p>
              </div>
              <button type="button" onClick={clearSig} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono font-semibold hover:text-[color:var(--primary)] transition-colors" data-testid="waiver-clear-sig">
                <RotateCcw size={12} /> {t("waiver.signature.clear")}
              </button>
            </div>
            <div className="border border-white/15 bg-white relative">
              <SignatureCanvas
                ref={sigRef}
                penColor="#0a0a0a"
                onEnd={() => setSigned(true)}
                canvasProps={{ className: "w-full h-48 md:h-60", "data-testid": "waiver-sig-canvas" }}
              />
              {!signed && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-black/30 font-display text-3xl md:text-4xl uppercase select-none">
                  ✎
                </div>
              )}
            </div>
          </section>

          {error && (
            <p className="text-[color:var(--primary)] text-sm font-semibold" data-testid="waiver-error">
              {error}
            </p>
          )}

          <div className="flex flex-col md:flex-row md:items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] disabled:opacity-60 text-white font-bold uppercase tracking-[0.16em] text-[11px] px-6 py-3.5 transition-colors"
              data-testid="waiver-submit"
            >
              {submitting ? t("waiver.submitting") : t("waiver.submit")} <ArrowRight size={13} />
            </button>
            <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-white/50">
              <ShieldCheck size={12} className="text-[color:var(--primary)]" /> Securely recorded for Rockstar ATV Rentals LLC
            </p>
          </div>
        </form>
      </main>
    </div>
  );
}

const inputCls =
  "w-full bg-black border border-white/15 focus:border-[color:var(--primary)] text-white px-3 py-2.5 text-sm outline-none transition-colors";

function Input({ label, children, full = false, testid }) {
  return (
    <label className={`${full ? "md:col-span-2" : ""} block`} data-testid={testid}>
      <span className="text-[9px] uppercase tracking-[0.26em] font-mono font-bold text-white/80 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Checkbox({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-[color:var(--primary)]" />
      <span className="text-xs md:text-sm text-white/75 leading-relaxed">{label}</span>
    </label>
  );
}

function Radio({ name, checked, onChange, label, testid }) {
  return (
    <label className={`flex items-start gap-2.5 cursor-pointer border p-3 transition-colors ${checked ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10" : "border-white/15"}`} data-testid={testid}>
      <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-1 h-4 w-4 accent-[color:var(--primary)]" />
      <span className="text-xs md:text-sm text-white/75 leading-relaxed">{label}</span>
    </label>
  );
}
